import React from 'react';
import groundImg from './football-ground.png';

const fieldStyle = {
  wrapper: {
    textAlign: 'center',
    maxWidth: '800px',
    margin: '0 auto',
    padding: '20px 0',
  },
  divisionName: {
    color: '#090C82',
    fontSize: '1rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '2px',
    marginBottom: '4px',
  },
  gameTitle: {
    color: '#090C82',
    fontSize: '1.5rem',
    fontWeight: 700,
    marginBottom: '4px',
  },
  gameDescription: {
    fontSize: '0.95rem',
    color: '#333',
    marginBottom: '24px',
  },
  fieldContainer: {
    position: 'relative',
    width: '100%',
    maxWidth: '500px',
    margin: '0 auto 30px',
  },
  fieldImage: {
    width: '100%',
    height: 'auto',
    display: 'block',
  },
  playerName: {
    position: 'absolute',
    color: '#fff',
    fontSize: '0.7rem',
    fontWeight: 600,
    textAlign: 'center',
    textShadow: '0 1px 3px rgba(0,0,0,0.7)',
    whiteSpace: 'nowrap',
    transform: 'translateX(-50%)',
    background: 'rgba(0,0,0,0.35)',
    padding: '2px 6px',
    borderRadius: '3px',
  },
  sectionLine: {
    fontSize: '0.9rem',
    marginBottom: '6px',
    color: '#333',
  },
  sectionLabel: {
    fontWeight: 700,
  },
  sectionNames: {
    color: '#090C82',
  },
};

/*
 * Player positions on the oval, expressed as percentage offsets
 * from the top-left of the image. These map to the 15 field positions
 * in the standard 5x3 grid order.
 */
const POSITION_COORDS = [
  // Row 1 (F): Forward Pocket Left, Full Forward, Forward Pocket Right
  { top: '10%', left: '15%' },
  { top: '10%', left: '50%' },
  { top: '10%', left: '85%' },
  // Row 2 (HF): Half Forward Left, Centre Half Forward, Half Forward Right
  { top: '26%', left: '15%' },
  { top: '26%', left: '50%' },
  { top: '26%', left: '85%' },
  // Row 3 (C): Wing Left, Centre, Wing Right
  { top: '44%', left: '15%' },
  { top: '44%', left: '50%' },
  { top: '44%', left: '85%' },
  // Row 4 (HB): Half Back Left, Centre Half Back, Half Back Right
  { top: '62%', left: '15%' },
  { top: '62%', left: '50%' },
  { top: '62%', left: '85%' },
  // Row 5 (B): Back Pocket Left, Full Back, Back Pocket Right
  { top: '80%', left: '15%' },
  { top: '80%', left: '50%' },
  { top: '80%', left: '85%' },
];

function formatPlayerLabel(player) {
  if (!player || !player.name) return '';
  let label = player.name;
  if (player.number) label += ' #' + player.number;
  if (player.isCaptain) label += ' (c)';
  return label;
}

function formatListSection(players) {
  if (!players || players.length === 0) return '';
  return players
    .filter((p) => p.name)
    .map((p) => formatPlayerLabel(p))
    .join(',  ');
}

function formatOutSection(players) {
  if (!players || players.length === 0) return '';
  return players
    .filter((p) => p.name)
    .map((p) => {
      let label = p.name;
      if (p.isCaptain) label += ' (c)';
      if (p.number) label += ' #' + p.number;
      if (p.reason) label += ' (' + p.reason + ')';
      return label;
    })
    .join(',  ');
}

export function FootballField({ divisionName, gameTitle, gameDescription, selectionData }) {
  const field = selectionData.field || [];
  const ruck = selectionData.ruck || [];
  const interchange = selectionData.interchange || [];
  const playerIn = selectionData.in || [];
  const playerOut = selectionData.out || [];

  return (
    <div style={fieldStyle.wrapper}>
      {divisionName && (
        <div style={fieldStyle.divisionName}>{divisionName} Team Selections</div>
      )}
      {gameTitle && <div style={fieldStyle.gameTitle}>{gameTitle}</div>}
      {gameDescription && <div style={fieldStyle.gameDescription}>{gameDescription}</div>}

      <div style={fieldStyle.fieldContainer}>
        <img src={groundImg} alt="Football ground" style={fieldStyle.fieldImage} />
        {field.map((player, idx) => {
          const label = formatPlayerLabel(player);
          if (!label || idx >= POSITION_COORDS.length) return null;
          const coords = POSITION_COORDS[idx];
          return (
            <div
              key={idx}
              style={{ ...fieldStyle.playerName, top: coords.top, left: coords.left }}
            >
              {label}
            </div>
          );
        })}
      </div>

      {ruck.length > 0 && (
        <div style={fieldStyle.sectionLine}>
          <span style={fieldStyle.sectionLabel}>Ruck - </span>
          <span style={fieldStyle.sectionNames}>{formatListSection(ruck)}</span>
        </div>
      )}
      {interchange.length > 0 && (
        <div style={fieldStyle.sectionLine}>
          <span style={fieldStyle.sectionLabel}>Interchange - </span>
          <span style={fieldStyle.sectionNames}>{formatListSection(interchange)}</span>
        </div>
      )}
      {playerIn.length > 0 && (
        <div style={fieldStyle.sectionLine}>
          <span style={fieldStyle.sectionLabel}>In - </span>
          <span style={fieldStyle.sectionNames}>{formatListSection(playerIn)}</span>
        </div>
      )}
      {playerOut.length > 0 && (
        <div style={fieldStyle.sectionLine}>
          <span style={fieldStyle.sectionLabel}>Out - </span>
          <span style={fieldStyle.sectionNames}>{formatOutSection(playerOut)}</span>
        </div>
      )}
    </div>
  );
}
